import { pickBy } from "lodash";

import { AssetDTO, AssetFilters } from "../models/assets";
import { AssetsAtomicQuery, AssetsAtomicQueryV2 } from "../models/bulkActions";
import {
  buildCustomFieldFilters,
  standardFieldMapKeys,
} from "../models/customFields";
import {
  PostBasedPaginatedRequest,
  PostBasedPaginatedResponse,
} from "../models/shared";
import { CursorState, Sort, Stringable } from "../types";

interface Idable {
  id: string;
}
export const getId = ({ id }: Idable) => id;

export const isNotUndefined = <T>(item: T | undefined): item is T =>
  item !== undefined;

export const buildSearchString = (
  params: Record<string, Stringable | Stringable[] | null | undefined>
): string =>
  Object.entries(params)
    .reduce((searchParams, [key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          searchParams.append(key, v.toString());
        });
      } else if (value !== undefined) {
        searchParams.append(
          key,
          value === null
            ? ""
            : typeof value === "string"
              ? value
              : value.toString()
        );
      }
      return searchParams;
    }, new URLSearchParams())
    .toString();

// The only standard attributes that are sortable are those in standardFieldMapKeys.
export const buildSortString = (sort: Sort) =>
  `${standardFieldMapKeys.includes(sort.key) ? "" : "customAttributes."}${
    sort.key
  } ${sort.order === 1 ? "asc" : "desc"}`;

export const fetchAllPaginatedResults = async <
  T,
  K extends PostBasedPaginatedRequest,
>(
  query: (cursorState: CursorState) => Promise<PostBasedPaginatedResponse<T, K>>
) => {
  let initialFetch = true;
  let cursorState: CursorState = undefined;
  let resp: PostBasedPaginatedResponse<T, K> | undefined;
  let results: T[] = [];

  while (initialFetch || cursorState) {
    initialFetch = false;
    resp = await query(cursorState);
    cursorState = resp.pagination.cursorState;
    results = results.concat(resp.results);
  }
  return { ...resp, results };
};

// Migrated from libs/acs-assets-shared-web/src/clients/helpers.ts (TODO T001.4.1)
export const buildQuery = (
  selectedAssetIds: AssetDTO["id"][],
  filters: AssetFilters,
  entityType: (
    | AssetsAtomicQuery
    | AssetsAtomicQueryV2
  )["entityType"] = "assets_atomic_query_v2",
  assetIds?: AssetDTO["id"][]
) => {
  // Bulk actions, export and quick reports have to act on the same set the list shows, so the
  // classification selection is flattened here the same way assetClient.query flattens it.
  const classificationNodeIds = Object.values(
    filters.classifications ?? {}
  ).flat();

  return {
    entityType,
    ...{
      includeFilters: pickBy(
        {
          ids: Array.isArray(assetIds) ? assetIds : undefined,
          categoryIds: Array.isArray(filters.categoryIds)
            ? filters.categoryIds
            : undefined,
          assignedCategoryIds: Array.isArray(filters.assignedCategoryIds)
            ? filters.assignedCategoryIds
            : undefined,
          statusIds: filters.statusIds,
          locationIds: filters.location ? [filters.location.id] : undefined,
          searchText: filters.searchText,
          customAttributes:
            filters.customFields && Object.keys(filters.customFields).length
              ? buildCustomFieldFilters(filters.customFields, true)
              : undefined,
          classificationNodeIds: classificationNodeIds.length
            ? classificationNodeIds
            : undefined,
          sourceModelLineageUrns: Array.isArray(filters.sourceModelLineageUrns)
            ? filters.sourceModelLineageUrns
            : undefined,
          parentSystemIds: Array.isArray(filters.parentSystemIds)
            ? filters.parentSystemIds
            : undefined,
          ancestorSystemIds: Array.isArray(filters.ancestorSystemIds)
            ? filters.ancestorSystemIds
            : undefined,
        },
        (value) => value !== undefined // remove undefined key-value pairs
      ),
      excludeFilters: selectedAssetIds.length
        ? {
            ids: selectedAssetIds,
          }
        : {},
    },
  };
};


